<template>
  <div class="step-three" v-loading="loading">
    <!-- 提示信息 -->
    <el-alert
      title="请确认以下信息，无误后点击「上架商品」"
      type="info"
      :closable="false"
      show-icon
    />

    <div class="section-card">
      <!-- 摘要栏 -->
      <div class="summary-bar">
        <div class="summary-item">
          <span class="label">签约主体：</span>
          <span class="value">{{ summaryData.enterpriseName }}</span>
        </div>
        <div class="summary-item">
          <span class="label">门店：</span>
          <span class="value"
            ><span class="highlight">{{ summaryData.storeCount }}</span>
            家</span
          >
        </div>
        <div class="summary-item">
          <span class="label">预计上架商品：</span>
          <span class="value"
            ><span class="highlight">{{ summaryData.productCount }}</span>
            个</span
          >
        </div>
      </div>

      <!-- 预览表格 -->
      <PreviewTable :apiFn="apiFn" />
    </div>
  </div>
</template>

<script setup>
import {
  getBatchPreview,
  getBatchPreviewGoodsList,
  submitBatchPublish,
} from '@/api/goods'
import { useEventBus } from '@/utils/useUtils'
import { onMounted, reactive, ref } from 'vue'
import PreviewTable from './components/PreviewTable.vue'
import { useBatchSession } from './hooks/useBatchSession'

const { sessionId } = useBatchSession()
const $eventBus = useEventBus()

const loading = ref(false)

// 摘要数据
const summaryData = reactive({
  enterpriseName: '',
  storeCount: 0,
  productCount: 0,
})

// API 函数 - 直接返回后端数据（遵循 base-list-management 最佳实践）
const apiFn = async (params) => {
  const { pageNum = 1, pageSize = 10 } = params || {}
  loading.value = true

  const res = await getBatchPreviewGoodsList({
    sessionId: sessionId.value,
    pageNum,
    pageSize,
  })

  loading.value = false

  // 如果有数据，每次分页都更新左侧预览（显示当前页的商品）
  if (res?.code === 1 && res.data?.items?.length) {
    updatePreview(res.data.items)
  }

  // 直接返回 API 原始响应，让 base-list-management 自己处理
  return res
}

// 更新左侧预览
const updatePreview = (items) => {
  if (!items?.length) return

  // 将前10个商品组映射为 productBasicInfo 格式（预览最多显示10个）
  const productBasicInfo = items.slice(0, 10).map((item) => {
    // 直接使用服务端返回的 topTags
    const topTags = item.topTags || []

    // 映射租赁方案
    const rentPlans = (item.rentPlanList || []).map((plan) => ({
      goodsId: plan.id,
      rentPeriod: plan.rentPeriod,
      rentPeriodType: plan.rentPeriodType,
      rentPeriodShowName: `${plan.rentPeriod}${plan.rentPeriodType === 1 ? '天' : '个月'}`,
      presentDays: plan.presentDays,
      rentAmountUnit: plan.rentAmountUnit,
      rentAmount: plan.rentAmount,
      depositPayMode: 6,
      deposit: plan.deposit,
      rentCategory: 1,
      state: plan.state,
    }))

    // 映射底部标签（合作平台）
    const bottomTags = item.cooperationPlatformList || []

    return {
      brandId: item.brandId,
      brand: item.brandName,
      seriesId: item.seriesId,
      carSeries: item.seriesName,
      productName: item.productName,
      nameFirstLetter: 'K',
      cityCode: item.cityCode,
      carType: item.operationType,
      qualificationInfo: item.carLevel,
      qualificationInfoName:
        item.operationType === 10 ? item.carLevelName : null,
      pictureUrl: item.pictureUrl || '',
      topTags,
      rentPlans,
      bottomTags,
    }
  })

  // 使用第一个商品组更新预览数据
  const firstItem = items[0]
  $eventBus.$emit('iframeDataChange', {
    cityCode: firstItem.cityCode,
    cityName: firstItem.cityName,
    operationTypeName: firstItem.operationTypeName,
    productBasicInfo,
  })
}

// 提交上架
const submit = async () => {
  const res = await submitBatchPublish({
    sessionId: sessionId.value,
  })

  if (res?.code !== 1) {
    // axios 已显示错误
    throw new Error('上架失败，请重试')
  }

  // TODO: 埋点上报
  // const elapsed = elapsedTime.value
  // reportTrack('batch_publish_success', { elapsedTime: elapsed })

  return Promise.resolve()
}

// 组件挂载时加载预览摘要
onMounted(async () => {
  // 获取预览摘要
  const res = await getBatchPreview({
    sessionId: sessionId.value,
  })

  if (res?.code === 1 && res.data) {
    summaryData.enterpriseName = res.data.corporationName || ''
    summaryData.storeCount = res.data.storeCount || 0
    summaryData.productCount = res.data.productCount || 0
  }

  // 商品列表数据由 PreviewTable 的 base-list-management 自动加载
  // apiFn 返回原始 API 响应，dataFilter 会自动转换数据格式
  // 每次分页时自动更新左侧预览（显示当前页商品）
})

defineExpose({
  submit,
})
</script>

<style lang="scss" scoped>
.step-three {
  :deep(.el-alert) {
    margin-top: 0;
    position: relative;
    z-index: 1;
    height: auto !important;
    min-height: auto !important;
    overflow: visible !important;
  }

  :deep(.el-alert__content) {
    font-size: 14px !important;
    line-height: 1.5 !important;
    height: auto !important;
  }
}

.summary-bar {
  display: flex;
  flex-direction: row;
  padding: 0px;
  flex-grow: 1;
  align-self: stretch;
  gap: 16px;
  line-height: 22px;

  .summary-item {
    display: flex;
    align-items: center;

    .label {
      color: #4e5969;
    }

    .value {
      color: #1d2129;
      font-weight: 600;

      .highlight {
        color: #2a6bff;
      }
    }
  }
}
</style>
